import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../patientForm.css';
import { Formik, Form, useFormik, ErrorMessage, Field } from 'formik';
import { ICONS, Loader, Select } from '@ohif/ui';
import { PatientPropsType } from '@custom/Utils/constants/interfaces';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _attachementUrls, _patientUrls } from '@custom/Utils/constants/apiUrls';
import { InsuranceAndIDFormSchema } from './schema';
import { servicesManager } from '../../../app/src/App';
import { useNavigate, useLocation } from 'react-router-dom';
import { customStyle } from '@custom/Utils/utils';

const InsuranceAndId = (props: PatientPropsType) => {

  const [file, setFile] = useState<File | undefined>(undefined);
  const [load, setLoad] = useState(false)
  const [fileName, setFileName] = useState('No file Chosen');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const data = useLocation()
  const formData = new FormData()

  const { uiNotificationService } = servicesManager.services

  const initialValues = useMemo(() => {
    if (data.state.mode === 'edit') {
      const patientDetails = data.state?.patientDetails?.patientDetail;
      setFileName(patientDetails?.identityInfo?.cardName ?? "No file Chosen");

      return {
        idCardType: patientDetails?.identityInfo?.cardType || "",
        idCardNumber: patientDetails?.identityInfo?.cardNo || "",
        idCardAttachement: patientDetails?.identityInfo?.cardUrl || "",
        hasInsurance: patientDetails?.insuranceInfo?.providerName ? 'Yes' : 'No',
        insuranceProviderName: patientDetails?.insuranceInfo?.providerName || "",
        insurancePolicyNumber: patientDetails?.insuranceInfo?.policyNo || "",
        groupNumber: patientDetails?.insuranceInfo?.groupNo || "",
        insuranceAuthorizationInfo: patientDetails?.insuranceInfo?.authorizeInfo || "",
      };
    }

    return {
      idCardType: props.patient?.identityInfo?.cardType || "",
      idCardNumber: props.patient?.identityInfo?.cardNo || "",
      idCardAttachement: props.patient?.identityInfo?.cardUrl || "",
      hasInsurance: props.patient?.insuranceInfo?.providerName ? 'Yes' : 'No',
      insuranceProviderName: props.patient?.insuranceInfo?.providerName || "",
      insurancePolicyNumber: props.patient?.insuranceInfo?.policyNo || "",
      groupNumber: props.patient?.insuranceInfo?.groupNo || "",
      insuranceAuthorizationInfo: props.patient?.insuranceInfo?.authorizeInfo || "",
    };
  }, [data.state, props.patient]);

  const handleFileSave = async (id: any) => {
    if (file) {
      formData.append('file', file)
      formData.append('type', 'IDCARD')
      formData.append('_id', id)
      const response = await apiRequest({ method: 'POST', endUrl: _attachementUrls.add, headerType: 'form', body: formData })
    }
  }

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    setFile(event.target.files[0])
    if (file) {
      setFileName(file.name); // Update the state with the selected file's name
    }
  };

  const handleSave = async (values) => {

    const payload = {
      "profileStep": props.PageNumber,
      "_id": props.patientDocID,
      "identityInfo": {
        "cardType": values.idCardType,
        "cardNo": values.idCardNumber,
        "cardUrl": values.idCardAttachement,
        "cardName": fileName
        // "ABHANo": values.AbhaNumber,
      },
      "insuranceInfo": {
        "providerName": values.insuranceProviderName,
        "policyNo": values.insurancePolicyNumber,
        "groupNo": values.groupNumber,
        "authorizeInfo": values.insuranceAuthorizationInfo
      }
    }
    if (values.hasInsurance == 'No') {
      payload.insuranceInfo = null
    }
    setLoad(true)
    const response = await apiRequest({ method: 'POST', endUrl: _patientUrls.add, headerType: 'json', body: payload })
    if (response.status) {
      setLoad(false)

      handleFileSave(props.patientDocID)
      props.setPageNumber((prev: any) => prev + 1)
    } else if (!response.status) {
      setLoad(false)

      uiNotificationService.show({
        title: "Error",
        type: "error",
        message: response.msg
        , position: "topRight"
      })
    }
  }

  const handleDivClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <section className='form__section'>
      <Formik
        initialValues={initialValues}
        validationSchema={InsuranceAndIDFormSchema}
        onSubmit={handleSave}
      >
        {({
          errors,
          values,
          handleChange,
          handleBlur,
          touched,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form >

            <h1 className='form__section__title'>Identification</h1>
            <div className='formRow formRow__3'>
              <div className='formCol'>
                <div className='form__Field'>
                  <label className="form__label" htmlFor='idCardType' >ID Card Type</label>
                  <select
                    id="idCardType"
                    name="idCardType"
                    className="form__select form__select--sm"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.idCardType}
                    style={customStyle.select}
                  >
                    <option value="">Select</option>
                    {props?.commonData?.idCardTypes?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ErrorMessage name="idCardType" component="div" className="error text-error" />
                </div>
              </div>
              <div className='formCol'>
                <div className="form__Field">
                  <label className="form__label" htmlFor='idCardNumber'>
                    ID Card Number
                  </label>
                  <input
                    type="text"
                    id="idCardNumber"
                    name="idCardNumber"
                    placeholder="ID Card Number"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.idCardNumber}
                    className="form__input form__input--sm"
                  />
                  <ErrorMessage name="idCardNumber" component="div" className="error text-error" />
                </div>
              </div>
              <div className='formCol'>
                <div className='form__Field'>
                  <label className='form__label' htmlFor='idCardAttachement'>
                    ID Card Attachment
                  </label>
                  <input
                    type="file"
                    id="idCardAttachement"
                    name="idCardAttachement"
                    placeholder="ID Card Attachement"
                    onChange={(e) => {
                      handleChange(e)
                      handleFileChange(e)
                    }}
                    onBlur={handleBlur}
                    style={{ display: "none" }}
                    ref={fileInputRef}
                    accept="image/*,application/pdf"
                  />
                  <div onClick={handleDivClick} className='customInput'>
                    <img src={`${ICONS.browse}`} alt='Browse' />
                    {fileName}
                  </div>
                  <ErrorMessage name="idCardAttachement" component="div" className="error text-error" />
                </div>
              </div>
            </div>
            <h1 className='form__section__title'>Insurance Information</h1>
            <div className='formRow formRow__2'>
              <div className="formCol">
                <div className='form__Field'>
                  <div className='form__radio'>
                    <label htmlFor='hasInsurance' className='form__label mr-2'>Do you have Insurance:</label>
                    <div className='form__radio__option mb_0'>
                      <input
                        type="radio"
                        id="hasInsurance"
                        name="hasInsurance"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={'Yes'}
                        className='form__radio__input'
                        checked={values.hasInsurance == 'Yes'}
                      />
                      <label className='form__radio__label' >Yes</label>
                    </div>
                    <div className='form__radio__option mb_0'>
                      <input
                        type="radio"
                        id="hasInsurance"
                        name="hasInsurance"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={'No'}
                        className='form__radio__input'
                        checked={values.hasInsurance == 'No'}
                      />
                      <label className='form__radio__label'>No</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='formRow formRow__4'>
              <div className="formCol">
                <div className='form__Field'>
                  <label className='form__label' htmlFor='insuranceProviderName' >Insurance Provider Name</label>
                  <select
                    id="insuranceProviderName"
                    name="insuranceProviderName"
                    className="form__select form__select--sm"
                    style={customStyle.select}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.insuranceProviderName}
                    disabled={values.hasInsurance == "No" ? true : false}
                  >
                    <option value="">Select</option>
                    {props.commonData.insuranceProviders.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className='formCol'>
                <div className="form__Field">
                  <label className='form__label' htmlFor='insurancePolicyNumber'>
                    Insurance Policy Number
                  </label>
                  <input
                    type="text"
                    id="insurancePolicyNumber"
                    name="insurancePolicyNumber"
                    className="form__input form__input--sm"
                    placeholder=" Insurance Policy Number"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.insurancePolicyNumber}
                    disabled={values.hasInsurance == "No" ? true : false}
                  />
                </div>
              </div>
              <div className="formCol">
                <div className="form__Field">
                  <label className='form__label' htmlFor='groupNumber'>
                    Group Number
                  </label>
                  <input
                    type="text"
                    id="groupNumber"
                    name="groupNumber"
                    placeholder="Group Number"
                    className="form__input form__input--sm"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.groupNumber}
                    disabled={values.hasInsurance == "No" ? true : false}
                  />
                </div>
              </div>
              <div className='formCol'>
                <div className="form__Field">
                  <label className='form__label' htmlFor='insuranceAuthorizationInfo'>
                    Insurance Authorization Information
                  </label>
                  <input
                    type="text"
                    id="insuranceAuthorizationInfo"
                    name="insuranceAuthorizationInfo"
                    placeholder="Insurance Authorization Information"
                    onChange={handleChange}
                    className="form__input form__input--sm"
                    onBlur={handleBlur}
                    value={values.insuranceAuthorizationInfo}
                    disabled={values.hasInsurance == "No" ? true : false}
                  />
                </div>
              </div>
            </div>


            <div className='btn__group'>
              <button
                className='btn btn__white'
                onClick={() => props.setPageNumber(prev => prev - 1)}
              >
                Previous
              </button>
              <button
                className='btn btn__primary'
                type="submit"
              >
                Next
              </button>
            </div>
          </ Form>
        )}
      </Formik>
      <Loader isLoaderOpen={load} />
    </section>
  )
}

export default InsuranceAndId
